import React from 'react';
import { useSelector } from 'react-redux';

/**
 * Custom hook to check if the current logged-in admin has specific permissions.
 * 
 * @returns {Object} { hasPermission, permissions, isAdmin }
 */
export const usePermission = () => {
  const { user } = useSelector((state) => state.auth);
  
  // Extract user data - handle both 'me' response (direct) and 'login' response (nested under admin)
  const userData = React.useMemo(() => user?.data?.admin || user?.data, [user]);
  
  const permissions = React.useMemo(() => userData?.permissions || [], [userData]);
  const roleSlug = React.useMemo(() => userData?.role_slug || userData?.role?.slug, [userData]);

  /**
   * Check if a specific permission exists.
   * 
   * @param {string} module - The module slug (e.g., 'user_management')
   * @param {string} action - The action slug (e.g., 'read', 'create', 'update', 'delete', 'export')
   * @returns {boolean}
   */
  const hasPermission = React.useCallback((module, action) => {
    // Super Admin has access to everything
    if (permissions.includes("*") || roleSlug === 'super_admin') {
      return true;
    }

    // Check for specific module:action permission
    return permissions.includes(`${module}:${action}`);
  }, [permissions, roleSlug]);

  /**
   * Check if user has ANY permission for a specific module (useful for showing/hiding menu groups)
   * 
   * @param {string} module - The module slug
   * @returns {boolean}
   */
  const hasAnyPermission = React.useCallback((module) => {
    if (permissions.includes("*") || roleSlug === 'super_admin') {
      return true;
    }
    return permissions.some(p => p.startsWith(`${module}:`));
  }, [permissions, roleSlug]);

  return { 
    hasPermission, 
    hasAnyPermission,
    permissions,
    role: userData?.role?.name || userData?.role,
    roleSlug
  };
};
